import React, { useCallback, useEffect, useState } from "react";
import { Image, Pressable, ScrollView, View } from "react-native";

import {
  BG_STYLES,
  BORDER_STYLES,
  Button,
  BUTTON_STATUS,
  COMMON_STYLES,
  HorizontalStepper,
  SIZE,
} from "@dehaat/dds";

import CropSelection from "../components/CropSelection";
import ConfirmDisease from "../components/ConfirmDisease";
import SuccessScreen from "../components/SuccessScreen";
import { StartDiagnosisScreenProps } from "app/farmer360/routes/types";
import { base64ToBlob } from "../helpers/common";
import ImgDetectModal from "../components/modals/ImgDetectModal";
import {
  apiToModalType,
  MODAL_CONFIG,
  ModalContent,
  ModalType,
} from "../utils/modalConfig";
import PoorImageQuality from "../components/modals/PoorImgQuality";
import createDiagnosis from "../apis/createDiagnosis";
import getPlantixResponse from "../apis/getPlantixResponse";
import { Crop, Disease, DiseaseDetailProps } from "../models/diagnosis";
import getDiseaseList from "../apis/getDiseaseList";
import fetchDiseaseDetail from "../apis/fetchDiseaseDetail";
import DiseaseDetail from "../components/DiseaseDetail";
import ImageCompareModal from "../components/modals/ImageCompareModal";
import { ROUTES as FARMER_360_ROUTES } from "app/farmer360/routes/types";
import {
  selectFarmerId,
  selectImageDataUrl,
  selectImageSourceType,
  selectImageUrl,
  selectLocation,
  selectPlantixUId,
  setPlantixUId,
} from "app/farmer360/store/app";
import { useAppDispatch, useAppSelector } from "app/hooks/reduxHooks";
import { rnNavigation } from "app/utils/appConfig";
import DiagnosisUnderProcess from "../components/DiagnosisUnderProcess";
import saveUserHistory from "../apis/saveUserHistory";
import { BACK } from "../assests/images";
import useImageUpload from "../hooks/useImageUpload";
import { CommonActions } from "@react-navigation/native";
import { trackEvent } from "app/farmer360/utils/tracking";
import { CustomEventTypes } from "../utils/events/CustomEventTypes";
import { useRedirectOnRefresh } from "../hooks/useRedirectOnRefresh";
import { useTranslation } from "react-i18next";

const StartDiagnosis = ({ route, navigation }: StartDiagnosisScreenProps) => {
  const { t } = useTranslation();
  const { cropId } = route.params || {};
  const [imageBlob, setImageBlob] = useState<Blob | null>(null);
  const imageUrl = useAppSelector(selectImageUrl);
  const imageDataUrl = useAppSelector(selectImageDataUrl);
  const imageSourceType = useAppSelector(selectImageSourceType);
  const [selectedCropId, setSelectedCropId] = useState<number | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [seeDetails, setSeeDetails] = useState(false);
  const [modalData, setModalData] = useState<ModalContent | null>(null);
  const [showPoorQualityModal, setShowPoorQualityModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [detectedCrops, setDetectedCrops] = useState<Crop[]>([]);
  const [diseaseList, setDiseaseList] = useState<Disease[]>([]);
  const [disease, setDisease] = useState<DiseaseDetailProps>();
  const [crop, setCrop] = useState<Crop>();
  const [showImgCompareModal, setShowImgCompareModal] = useState(false);
  const [selectedDiseaseImg, setSelectedDiseaseImg] = useState<string[] | null>(
    null
  );
  const [diagnosisUnderProcess, setDiagnosisUnderProcess] = useState(false);
  const selectedLocation = useAppSelector(selectLocation);
  const plantixId = useAppSelector(selectPlantixUId);

  const dispatch = useAppDispatch();
  const userId = useAppSelector(selectFarmerId);

  const routes = navigation.getState().routes;
  const firstRoute = routes[0];
  const secondRoute = routes[1];

  useEffect(() => {
    if (imageDataUrl) setImageBlob(base64ToBlob(imageDataUrl));
  }, [imageDataUrl]);

  const handleAddCrop = (crop: Crop) => {
    setSelectedCropId(crop.crop_id);
    setCrop(crop);
  };

  const { uploadFromCamera, uploadFromGallery } = useImageUpload(() => {
    navigation.dispatch(
      CommonActions.reset({
        index: 1,
        routes: [
          { name: firstRoute.name },
          { name: secondRoute.name },
          {
            name: FARMER_360_ROUTES.START_DIAGNOSIS,
          },
        ],
      })
    );
  });

  const addCropFromCamera = () => {
    uploadFromCamera();
  };

  const addCropFromGalary = () => {
    uploadFromGallery();
  };
  const handleTickPress = useCallback(async () => {
    const MIN_DISPLAY_TIME = 5000;
    const startTime = Date.now();
    setDiagnosisUnderProcess(true);
    try {
      const formData = new FormData();
      if (!userId) {
        return;
      }
      formData.append("crop", cropId);
      formData.append("farmer_id", userId.toString());
      formData.append("source", "MOBILE");
      formData.append(
        "latitude",
        (Math.floor(selectedLocation.latitude! * 100) / 100).toString()
      );
      formData.append(
        "longitude",
        (Math.floor(selectedLocation.longitude! * 100) / 100).toString()
      );
      formData.append(
        "application_used_image_gallery",
        imageSourceType.toString()
      );

      if (imageBlob) {
        const file = new File([imageBlob], "plant.jpg", { type: "image/jpeg" });
        formData.append("image", file);
      } else {
        return;
      }
      const plantix_id = await createDiagnosis(formData);
      if (!plantix_id) {
        setDiagnosisUnderProcess(false);
        setShowModal(true);
        setModalData(MODAL_CONFIG["SOMETHING_WENT_WRONG"]);
        return;
      }

      dispatch(setPlantixUId(plantix_id));

      const analysisResponse = await getPlantixResponse(plantix_id);
      const elapsed = Date.now() - startTime;
      const remainingTime = Math.max(0, MIN_DISPLAY_TIME - elapsed);

      setTimeout(() => {
        setDiagnosisUnderProcess(false);
      }, remainingTime);

      if (analysisResponse?.status === 1) {
        setShowModal(true);
        setModalData(MODAL_CONFIG["HEALTHY_CROP"]);
      } else if (analysisResponse?.status === 0) {
        setShowModal(true);
        setModalData(MODAL_CONFIG["CROP_NOT_SUPPORTED"]);
      }

      if (analysisResponse?.errors) {
        const apiErrorType = analysisResponse.errors[0]?.type;
        if (apiErrorType === "bad_image") {
          setShowPoorQualityModal(true);
        } else if (apiToModalType[apiErrorType]) {
          const modalType: ModalType = apiToModalType[apiErrorType];
          setModalData(MODAL_CONFIG[modalType]);
          setShowModal(true);
        }
      }

      if (analysisResponse?.crops?.length! > 0) {
        setDetectedCrops((prev) => {
          const newCrops = analysisResponse?.crops ?? prev;

          if (JSON.stringify(prev) !== JSON.stringify(newCrops)) {
            setActiveIndex((prevIndex) => prevIndex + 1);
            return newCrops;
          }

          return prev;
        });
      }
    } catch (err) {
      console.error("❌ API flow failed:", err);
    }
  }, [
    dispatch,
    imageBlob,
    imageSourceType,
    selectedLocation.latitude,
    selectedLocation.longitude,
    userId,
  ]);

  const handleImagePress = (item: string[]) => {
    setShowImgCompareModal(true);
    setSelectedDiseaseImg(item);
  };

  const handleRetake = () => {
    setShowModal(false);
    if (showPoorQualityModal) {
      setShowPoorQualityModal(false);
    }
    rnNavigation({
      name: FARMER_360_ROUTES.DISEASE_DIAGNOSIS,
    });
  };

  const fetchDiseaseList = async () => {
    const response = await getDiseaseList(plantixId!, selectedCropId!);
    if (response) {
      setDiseaseList(response);
      setActiveIndex(activeIndex + 1);
    } else {
      setShowModal(true);
      setModalData(MODAL_CONFIG["CROP_NOT_SUPPORTED"]);
    }
  };

  const handleDiagnosisClick = async (disease: Disease) => {
    trackEvent(CustomEventTypes.CropDocImageDiseaseSelected, {
      PlantixID: plantixId,
      CropID: crop?.crop_id,
      CropName: crop?.crop_name_local,
      DiseaseID: disease?.disease_id,
      DiseaseName: disease.disease_name,
    });
    const response = await fetchDiseaseDetail(plantixId!, disease.disease_id);
    if (response) {
      setDisease(response);
      setSeeDetails(true);
      setActiveIndex(activeIndex + 1);
    }
  };

  const getDiseaseTreatment = async () => {
    trackEvent(CustomEventTypes.CropDocImageUserCompletesDiagnosis, {
      PlantixID: plantixId,
      CropID: crop?.crop_id,
      CropName: crop?.crop_name_local,
      DiseaseID: disease?.disease_id,
      DiseaseName: disease?.disease_name,
    });
    const response = await saveUserHistory(
      userId!,
      plantixId!,
      selectedCropId!,
      disease?.disease_id!
    );

    if (response?.message === "OK") {
      rnNavigation({
        name: FARMER_360_ROUTES.DISEASE_TREATMENT,
        params: {
          diseaseId: disease?.disease_id!,
          cropName: crop?.crop_name_local!,
          cropId: crop?.crop_id!,
        },
      });
    } else {
      setShowModal(true);
      setModalData(MODAL_CONFIG["SOMETHING_WENT_WRONG"]);
    }
  };

  useEffect(() => {
    navigation.setOptions({
      headerLeft: () => (
        <Pressable
          style={[COMMON_STYLES["ml3/4"]]}
          onPress={
            seeDetails
              ? () => {
                  trackEvent(CustomEventTypes.BackButtonClick, {
                    PageLocation: "Disease Detail",
                  });
                  setActiveIndex(activeIndex - 1);
                  setSeeDetails(false);
                }
              : () => {
                  trackEvent(CustomEventTypes.BackButtonClick, {
                    PageLocation: "Disease Selection",
                  });
                  navigation.goBack();
                }
          }
        >
          <Image source={BACK} style={{ width: 24, height: 24 }} />
        </Pressable>
      ),
    });
    if (diagnosisUnderProcess) {
      navigation.setOptions({ headerShown: false });
      return;
    }

    if (activeIndex === 0) {
      navigation.setOptions({ headerShown: false });
    } else if (activeIndex >= 2) {
      if (showImgCompareModal) {
        navigation.setOptions({
          headerShown: false,
        });
      } else {
        navigation.setOptions({
          headerShown: true,
          title: "Confirm the disease",
        });
      }
    } else {
      navigation.setOptions({
        headerShown: true,
        title: "Select Crop",
      });
      trackEvent(CustomEventTypes.CropDocImageCropSelectionUserLanded, {
        PlantixID: plantixId,
      });
    }
  }, [
    activeIndex,
    navigation,
    showImgCompareModal,
    diagnosisUnderProcess,
    seeDetails,
    plantixId,
  ]);

  useEffect(() => {
    if (!imageSourceType) {
      trackEvent(CustomEventTypes.CropDocImageConfirmed, {
        selectiontype: "camera",
      });
      handleTickPress();
    }
  }, [imageSourceType, handleTickPress]);

  return (
    <>
      {diagnosisUnderProcess ? (
        <DiagnosisUnderProcess />
      ) : activeIndex === 0 && imageSourceType ? (
        <SuccessScreen
          imageUri={imageUrl}
          handleClose={() => {
            trackEvent(CustomEventTypes.CropDocImageRejected, {
              selectiontype: "gallery",
            });
            navigation.goBack();
          }}
          handleTickPress={() => {
            trackEvent(CustomEventTypes.CropDocImageConfirmed, {
              selectiontype: "gallery",
            });
            handleTickPress();
          }}
        />
      ) : showModal || showPoorQualityModal ? null : (
        <View
          style={[COMMON_STYLES.pt1, BG_STYLES.bgWhite, COMMON_STYLES.flex1]}
        >
          <HorizontalStepper
            numberOfSteps={3}
            activeIndex={activeIndex}
            titles={[t("click_photo"), t("select_crop"), t("see_diagnosis")]}
          />
          <ScrollView>
            {activeIndex === 1 && (
              <CropSelection
                selectedCropId={selectedCropId}
                handleAddCrop={handleAddCrop}
                detectedCrops={detectedCrops}
                addCropFromCamera={addCropFromCamera}
                addCropFromGalary={addCropFromGalary}
              />
            )}
            {activeIndex === 2 && (
              <ConfirmDisease
                diseases={diseaseList}
                onBtnClick={handleDiagnosisClick}
                onImagePress={handleImagePress}
                crop={crop!}
                onEdit={() => {
                  setActiveIndex(1);
                  trackEvent(
                    CustomEventTypes.CropDocImageDiseaseSelectionCropEditClick,
                    {
                      PlantixID: plantixId,
                      CropID: crop?.crop_id,
                      CropName: crop?.crop_name_local,
                    }
                  );
                }}
              />
            )}
            {seeDetails && (
              <View
                style={[
                  { borderTopWidth: 16 },
                  BORDER_STYLES.borderNeutral10,
                  COMMON_STYLES.mt1,
                ]}
              >
                <DiseaseDetail
                  disease={disease!}
                  onImagePress={handleImagePress}
                  cropId={crop?.crop_id!}
                  cropName={crop?.crop_name_local!}
                />
              </View>
            )}
          </ScrollView>
          {(activeIndex === 1 || seeDetails) && (
            <View
              style={[
                COMMON_STYLES.p1,
                {
                  shadowColor: "#000",
                  shadowOffset: { width: 0, height: -3 },
                  shadowOpacity: 0.1,
                  shadowRadius: 4,
                  elevation: 5,
                },
              ]}
            >
              <Button
                onClick={() => {
                  activeIndex === 1
                    ? fetchDiseaseList()
                    : getDiseaseTreatment();
                }}
                text={
                  activeIndex === 1 ? t("confirm") : t("confirm_see_treatment")
                }
                size={SIZE.large}
                status={
                  !selectedCropId
                    ? BUTTON_STATUS.DISABLED
                    : BUTTON_STATUS.ACTIVE
                }
              />
            </View>
          )}
        </View>
      )}

      {modalData && showModal && (
        <ImgDetectModal
          onClose={handleRetake}
          showModal={showModal}
          imgSource={modalData?.imgSource?.[0]}
          title={modalData.title}
          description={modalData.description}
          actualImg={modalData.imgSource?.[1] || null}
          distortedImg={modalData.imgSource?.[2] || null}
          actualImgText={modalData.actualText || ""}
          distortedImgText={modalData.distortedImgText || ""}
        />
      )}

      {showPoorQualityModal && (
        <PoorImageQuality
          onRetry={handleRetake}
          showModal={showPoorQualityModal}
        />
      )}

      {showImgCompareModal && (
        <ImageCompareModal
          setShowImgCompareModal={setShowImgCompareModal}
          selectedDiseaseImg={selectedDiseaseImg}
        />
      )}
    </>
  );
};

export default StartDiagnosis;
